import React, { useState } from 'react';
import { Platform } from '../constants/platforms';
import { X, Copy, Download, ExternalLink, CheckCircle2, Check, LayoutGrid, Link2, Share2 } from 'lucide-react';

const PublishModal = ({ post, isOpen, onClose, onConfirm }) => {
  const [copied, setCopied] = useState(false);
  const [postUrl, setPostUrl] = useState('');

  if (!isOpen || !post) return null;

  // Safely get hashtags as array
  const getHashtagsArray = () => {
    if (!post.hashtags) return [];
    if (Array.isArray(post.hashtags)) return post.hashtags;
    if (typeof post.hashtags === 'string') return post.hashtags.split(/[,\s]+/).filter(Boolean);
    return [];
  };

  // Ensure hashtags start with #
  const hashtags = getHashtagsArray();
  const formattedHashtags = hashtags.map(tag => tag.startsWith('#') ? tag : `#${tag}`).join(' ');
  const fullCaption = `${post.content || ''}\n\n${formattedHashtags}`.trim();

  const handleCopy = () => {
    navigator.clipboard.writeText(fullCaption);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadImage = () => {
    if (!post.imageUrl) return;
    const link = document.createElement('a');
    link.href = post.imageUrl;
    link.download = `${post.platform.toLowerCase()}-post-${post.date}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getPlatformUrl = () => {
    const text = encodeURIComponent(fullCaption);
    const platform = post.platform?.toLowerCase();
    
    switch (platform) {
      case 'twitter':
      case 'x':
        return `https://twitter.com/intent/tweet?text=${text}`;
      case 'linkedin':
        return `https://www.linkedin.com/feed/`;
      case 'facebook':
        return `https://www.facebook.com/`;
      case 'instagram':
        return `https://www.instagram.com/`;
      case 'tiktok':
        return `https://www.tiktok.com/upload`;
      case 'bluesky':
        return `https://bsky.app/`;
      default:
        return 'https://google.com';
    }
  };

  const isMetaPlatform = post.platform?.toLowerCase() === 'facebook' || post.platform?.toLowerCase() === 'instagram';

  const handleMarkAsPosted = () => {
    // Pass the post URL along with the confirmation
    onConfirm(postUrl.trim() || null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="publish-modal">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-6 border-b border-slate-800 flex justify-between items-center bg-slate-800/50">
          <div>
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              Publish to {post.platform}
            </h3>
            <p className="text-sm text-slate-400 mt-1">Follow these steps to post manually</p>
          </div>
          <button 
            onClick={onClose}
            data-testid="close-modal-btn"
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors text-slate-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-grow space-y-8">
          
          {/* Step 1: Image */}
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="w-8 h-8 rounded-full bg-indigo-900/50 text-indigo-400 flex items-center justify-center shrink-0 border border-indigo-500/30 font-bold">1</div>
            <div className="flex-grow space-y-3">
              <h4 className="font-medium text-slate-200">Download Visual</h4>
              {post.imageUrl ? (
                <div className="flex gap-4 items-start">
                  <img 
                    src={post.imageUrl} 
                    alt="Post visual" 
                    className="w-32 h-32 object-cover rounded-lg border border-slate-700 bg-slate-800"
                  />
                  <button 
                    onClick={handleDownloadImage}
                    data-testid="download-image-btn"
                    className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-200 transition-colors"
                  >
                    <Download className="w-4 h-4" /> Download Image
                  </button>
                </div>
              ) : (
                <p className="text-sm text-amber-400 bg-amber-900/20 px-3 py-2 rounded-lg border border-amber-900/50">
                  No image generated yet. You can generate one in the dashboard before publishing.
                </p>
              )}
            </div>
          </div>

          {/* Step 2: Caption */}
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="w-8 h-8 rounded-full bg-indigo-900/50 text-indigo-400 flex items-center justify-center shrink-0 border border-indigo-500/30 font-bold">2</div>
            <div className="flex-grow space-y-3 w-full">
              <div className="flex justify-between items-center">
                <h4 className="font-medium text-slate-200">Copy Caption</h4>
                <button 
                  onClick={handleCopy}
                  data-testid="copy-caption-btn"
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    copied 
                    ? 'bg-green-900/30 text-green-400 border border-green-800' 
                    : 'bg-indigo-600 hover:bg-indigo-500 text-white'
                  }`}
                >
                  {copied ? <><Check className="w-3 h-3" /> Copied!</> : <><Copy className="w-3 h-3" /> Copy Text</>}
                </button>
              </div>
              <div className="bg-slate-950 p-4 rounded-lg border border-slate-800 text-slate-300 text-sm font-mono whitespace-pre-wrap max-h-40 overflow-y-auto">
                {fullCaption}
              </div>
            </div>
          </div>

          {/* Step 3: Platform */}
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="w-8 h-8 rounded-full bg-indigo-900/50 text-indigo-400 flex items-center justify-center shrink-0 border border-indigo-500/30 font-bold">3</div>
            <div className="flex-grow space-y-3">
              <h4 className="font-medium text-slate-200">Post to Platform</h4>
              <p className="text-sm text-slate-400 mb-3">
                Use native share or click to open {post.platform} in a new tab.
              </p>
              
              <div className="flex flex-wrap gap-3">
                {/* Native Share Button - Works great on mobile! */}
                {navigator.share && (
                  <button 
                    onClick={async () => {
                      try {
                        const shareData = {
                          title: `${post.platform} Post`,
                          text: fullCaption,
                        };
                        // If there's an image, try to share it too
                        if (post.imageUrl && navigator.canShare) {
                          try {
                            const response = await fetch(post.imageUrl);
                            const blob = await response.blob();
                            const file = new File([blob], 'post-image.png', { type: blob.type });
                            if (navigator.canShare({ files: [file] })) {
                              shareData.files = [file];
                            }
                          } catch (e) {
                            console.log('Could not share image:', e);
                          }
                        }
                        await navigator.share(shareData);
                      } catch (err) {
                        if (err.name !== 'AbortError') {
                          console.error('Share failed:', err);
                        }
                      }
                    }}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white rounded-lg font-medium transition-colors shadow-lg"
                  >
                    <Share2 className="w-4 h-4" /> Share to App
                  </button>
                )}
                
                {isMetaPlatform && (
                  <a 
                    href="https://business.facebook.com/latest/composer" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors border border-slate-600 shadow-md"
                  >
                    <LayoutGrid className="w-4 h-4" /> Meta Business Suite
                  </a>
                )}
                
                <a 
                  href={getPlatformUrl()} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  data-testid="open-platform-btn"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors shadow-lg shadow-blue-900/20"
                >
                  Open {post.platform} <ExternalLink className="w-4 h-4" />
                </a>
              </div>
              
              {/* Tip for mobile users */}
              {navigator.share && (
                <p className="text-xs text-indigo-400 mt-2">
                  💡 Tip: "Share to App" opens your device's share menu - pick Facebook, Twitter, etc!
                </p>
              )}
            </div>
          </div>

          {/* Step 4: Post URL (Optional) */}
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="w-8 h-8 rounded-full bg-green-900/50 text-green-400 flex items-center justify-center shrink-0 border border-green-500/30 font-bold">4</div>
            <div className="flex-grow space-y-3 w-full">
              <h4 className="font-medium text-slate-200 flex items-center gap-2">
                <Link2 className="w-4 h-4" /> Paste Post URL <span className="text-xs text-slate-500 font-normal">(optional)</span>
              </h4>
              <p className="text-sm text-slate-400">
                After posting, paste the URL to your published post. Team members can click to view and engage!
              </p>
              <input
                type="url"
                value={postUrl}
                onChange={(e) => setPostUrl(e.target.value)}
                placeholder="https://twitter.com/yourhandle/status/123..."
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder:text-slate-600 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
              />
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-800 bg-slate-800/50 flex justify-end gap-3">
          <button 
            onClick={onClose}
            className="px-4 py-2 text-slate-400 hover:text-white font-medium text-sm transition-colors"
          >
            Cancel
          </button>
          <button 
            onClick={handleMarkAsPosted}
            data-testid="mark-posted-btn"
            className="px-6 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg font-medium text-sm flex items-center gap-2 shadow-lg shadow-green-900/20 transition-all hover:scale-105"
          >
            <CheckCircle2 className="w-4 h-4" /> Mark as Posted
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublishModal;
